import { useState, useMemo } from 'react';
import { B, font, radius, fonts, weight } from '../constants/brand';
import { api } from '../api/client';
import { fmt } from '../utils/format';
import { useApi } from '../hooks/useApi';
import { Btn, Spinner, ErrorMsg, Table, PageHeader, Icon, Badge, StatusBadge } from '../components/ui';
import { todayIsoInZone } from '@harvus/shared';

const fmtAcres = (v) => v != null ? Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 }) : '—';

// Strip HTML → collapsed plain text (for the summary preview + CSV).
const htmlToText = (html) => (html || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/\s+/g, ' ').trim();
const truncate = (s, n) => { const x = (s || '').trim(); return x.length > n ? x.slice(0, n) + '…' : x; };

// One-line description of a prospect for the CSV / compact display.
const prospectLine = (p) => [p.customer || 'Unnamed', p.status, p.salesPrice != null ? fmt.currency(p.salesPrice) : null]
  .filter(Boolean).join(' · ');

// Plain-text summary of a row's note/task activity — used for CSV export.
const summaryText = (row) => {
  const counts = `${row.noteCount} note${row.noteCount !== 1 ? 's' : ''}, ${row.openTaskCount}/${row.taskCount} open tasks`;
  const digest = row.summary?.content ? htmlToText(row.summary.content) : '';
  return digest ? `${counts}. ${digest}` : counts;
};

// Report columns — single source of truth for the CSV export.
const CSV_COLUMNS = [
  { label: 'Deal',            value: r => r.dealName || '' },
  { label: 'Deal Detail',     value: r => r.name || '' },
  { label: 'Hold Type',       value: r => r.holdType || '' },
  { label: 'Acres',           value: r => r.acres ?? '' },
  { label: 'Total Cost',      value: r => r.totalCost ?? '' },
  { label: 'Prospect Count',  value: r => r.prospectCount ?? 0 },
  { label: 'Prospects',       value: r => (r.prospects || []).map(prospectLine).join(' | ') },
  { label: 'Notes',           value: r => r.noteCount ?? 0 },
  { label: 'Open Tasks',      value: r => r.openTaskCount ?? 0 },
  { label: 'Total Tasks',     value: r => r.taskCount ?? 0 },
  { label: 'Note/Task Summary', value: r => summaryText(r) },
];

function exportCsv(rows) {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = CSV_COLUMNS.map(c => c.label).join(',');
  const body = rows.map(r => CSV_COLUMNS.map(c => esc(c.value(r))).join(',')).join('\n');
  const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `real-estate-report-${todayIsoInZone()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// Prospects cell — count badge + compact per-offer list.
const ProspectsCell = ({ prospects, count }) => {
  if (!count) return <span style={{ color: B.textLt }}>—</span>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {(prospects || []).map(p => (
        <div key={p.prospectID} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: font.sm, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: weight.medium, color: B.text }}>{p.customer || 'Unnamed'}</span>
          {p.status && <StatusBadge status={p.status} />}
          {p.salesPrice != null && <span style={{ color: B.textMd }}>{fmt.currency(p.salesPrice)}</span>}
          {p.acres != null && <span style={{ color: B.textLt }}>· {fmtAcres(p.acres)} ac</span>}
        </div>
      ))}
    </div>
  );
};

// Note/Task summary cell — counts line + cached Claude digest preview.
const SummaryCell = ({ row }) => {
  const [expanded, setExpanded] = useState(false);
  const digest = row.summary?.content ? htmlToText(row.summary.content) : '';
  const stale = row.summary && row.summary.status && row.summary.status !== 'ready';
  return (
    <div style={{ maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 5 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <Badge color={B.navy}>{row.noteCount} note{row.noteCount !== 1 ? 's' : ''}</Badge>
        <Badge color={row.openTaskCount > 0 ? B.amber : B.textMd}>{row.openTaskCount}/{row.taskCount} open tasks</Badge>
        {stale && <span style={{ fontSize: font.xs, color: B.textLt, fontStyle: 'italic' }}>summary updating…</span>}
      </div>
      {digest ? (
        <div style={{ fontSize: font.sm, color: B.textMd, lineHeight: 1.5 }}>
          {expanded ? digest : truncate(digest, 180)}
          {digest.length > 180 && (
            <button onClick={() => setExpanded(v => !v)}
              style={{ background: 'none', border: 'none', padding: '0 0 0 4px', color: B.navy, cursor: 'pointer', fontFamily: 'inherit', fontSize: font.sm, fontWeight: weight.medium }}>
              {expanded ? 'less' : 'more'}
            </button>
          )}
        </div>
      ) : (
        <span style={{ fontSize: font.sm, color: B.textLt }}>No summary yet</span>
      )}
    </div>
  );
};

export const RealEstateReport = ({ onDealClick }) => {
  const [filterStatus, setFilterStatus] = useState('All');
  const { data, loading, error, reload } = useApi(
    () => api.get(`reports/real-estate-report${filterStatus !== 'All' ? `?status=${encodeURIComponent(filterStatus)}` : ''}`),
    [filterStatus]
  );
  const [q, setQ] = useState('');

  const rows = useMemo(() => {
    const all = data || [];
    const needle = q.trim().toLowerCase();
    if (!needle) return all;
    return all.filter(r =>
      [r.name, r.dealName, r.holdType, ...(r.prospects || []).map(p => p.customer)]
        .some(v => (v || '').toString().toLowerCase().includes(needle))
    );
  }, [data, q]);

  const totals = useMemo(() => ({
    acres: rows.reduce((s, r) => s + (Number(r.acres) || 0), 0),
    cost:  rows.reduce((s, r) => s + (Number(r.totalCost) || 0), 0),
  }), [rows]);

  const columns = [
    {
      key: 'name', label: 'Deal Detail',
      render: (_v, r) => (
        <div>
          {r.dealID && onDealClick ? (
            <button onClick={() => onDealClick(r.dealID)}
              style={{ background: 'none', border: 'none', padding: 0, color: B.navy, cursor: 'pointer', fontFamily: 'inherit', fontSize: font.base, fontWeight: weight.semibold, textAlign: 'left', textDecoration: 'underline' }}>
              {r.name}
            </button>
          ) : <span style={{ fontWeight: weight.semibold }}>{r.name}</span>}
          <div style={{ fontSize: font.xs, color: B.textLt, marginTop: 2 }}>{r.dealName}</div>
        </div>
      ),
    },
    { key: 'holdType', label: 'Hold Type', render: (v) => v ? <span style={{ fontSize: font.sm, color: B.textMd }}>{v}</span> : <span style={{ color: B.textLt }}>—</span> },
    { key: 'acres', label: 'Acres', align: 'right', render: (v) => fmtAcres(v) },
    { key: 'totalCost', label: 'Total Cost', align: 'right', render: (v) => v != null ? fmt.currency(v) : '—' },
    { key: 'prospects', label: 'Prospects', render: (_v, r) => <ProspectsCell prospects={r.prospects} count={r.prospectCount} /> },
    { key: 'summary', label: 'Note / Task Summary', render: (_v, r) => <SummaryCell row={r} /> },
  ];

  return (
    <div>
      <PageHeader
        icon="realEstateReport"
        title="Real Estate Report"
        subtitle={`${rows.length} propert${rows.length !== 1 ? 'ies' : 'y'} · ${fmtAcres(totals.acres)} acres · ${fmt.currency(totals.cost)} total cost`}
        actions={
          <>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <span style={{ position: 'absolute', left: 10, display: 'flex', color: B.textLt, pointerEvents: 'none' }}><Icon name="search" size={15} /></span>
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search deal, detail, customer…"
                style={{ padding: '8px 12px 8px 32px', borderRadius: radius.md, border: `1px solid ${B.border}`, fontSize: font.base, minWidth: 240, fontFamily: fonts.sans, background: B.surface, outline: 'none' }}
              />
            </div>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              style={{ padding: '8px 11px', border: `1px solid ${B.border}`, borderRadius: radius.md, fontSize: font.base, fontFamily: fonts.sans, background: B.surface, outline: 'none' }}>
              <option value="All">All Statuses</option>
              {['Prospect', 'Active'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <Btn size="sm" variant="ghost" onClick={reload}><Icon name="refresh" size={14} /> Refresh</Btn>
            <Btn size="sm" disabled={rows.length === 0} onClick={() => exportCsv(rows)}><Icon name="download" size={14} /> Export CSV</Btn>
          </>
        }
      />

      {error ? <ErrorMsg msg={error} onRetry={reload} /> : loading ? <Spinner /> : (
        <Table columns={columns} data={rows} />
      )}
    </div>
  );
};
